import { useId, useRef, useState } from 'react';

import { gruppiereTeile } from '../../geld';
import { formatiereEuro } from '../../warenkorb';
import { tastaturZuZiffer, type ZiffernTaste } from '../../ziffern';
import { ZiffernTasten } from '../Ziffernblock';
import {
  barTasten,
  barZustand,
  euroKurz,
  fehlbetragCent,
  MUENZEN_V2,
  SCHEINE_V2,
  schnellauswahl,
  stueckelungText,
} from './logik';
import { Kuerzel, ZahlungsHuelle } from './ZahlungsHuelle';

/** Wortlaut der Fehlerkorrektur FK (freigegeben 25.10.2026) – unverändert wie im BarDialog. */
export const FK_WARNTEXT =
  'Verkauf noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus prüfen.';

/**
 * Bar-Dialog V2 („C + Ziffernblock aus B“): reine Darstellung. Alle Werte (gegeben, Rückgeld,
 * lesbar, plausibel, kassierbar) und alle Aktionen kommen unverändert aus `BarDialog`; dieser
 * Baustein entscheidet nichts über das Kassieren. „Kassieren“ ruft genau dieselbe Funktion wie
 * der bisherige Dialog – mit derselben Bedingung `kassierbar`.
 */
export function BarDialogV2(props: {
  summeCent: number;
  laeuft: boolean;
  ungeklaert: string | null;
  gegebenCent: number | null;
  gegebenText: string;
  teile: readonly number[];
  rueckgeldCent: number | null;
  unlesbar: boolean;
  plausibel: boolean;
  kassierbar: boolean;
  onKassieren: () => void;
  onAbbrechen: () => void;
  onBelegPruefen: () => void;
  onSetze: (cent: number | null) => void;
  onZiffer: (taste: ZiffernTaste) => void;
  onTippe: (cent: number) => void;
  onLetztesZurueck: () => void;
  onEingabe: (text: string) => void;
}) {
  const zustand = barZustand({
    ungeklaert: props.ungeklaert !== null,
    laeuft: props.laeuft,
    unlesbar: props.unlesbar,
    plausibel: props.plausibel,
    gegebenCent: props.gegebenCent,
    rueckgeldCent: props.rueckgeldCent,
  });
  const regel = barTasten(zustand);
  const feld = useRef<HTMLInputElement | null>(null);
  const pruefen = useRef<HTMLButtonElement | null>(null);
  const feldId = useId();
  const statusId = useId();
  const scheinId = useId();
  const [scheineOffen, setScheineOffen] = useState(props.teile.length > 0);
  const gesperrt = props.laeuft || props.ungeklaert !== null;

  // FK-1 erst, wenn keine Anfrage mehr läuft (der Vorgangsmerker steht schon beim Senden)
  if (props.ungeklaert !== null && !props.laeuft) {
    return (
      <ZahlungsHuelle
        art="Barzahlung"
        betragLabel="Zu bezahlen"
        betragCent={props.summeCent}
        regel={regel}
        beschaeftigt={props.laeuft}
        klasse="zv2-bar zv2-ungeklaert"
        startFokus={pruefen}
        fokusSchluessel="ungeklaert"
        fuss={
          <>
            <button
              type="button"
              className="taste zweit zv2-abbrechen"
              disabled={props.laeuft}
              aria-keyshortcuts="Escape"
              onClick={props.onAbbrechen}
            >
              Schließen <Kuerzel>Esc</Kuerzel>
            </button>
            <button
              ref={pruefen}
              type="button"
              className="taste gross zv2-primaer"
              disabled={props.laeuft}
              onClick={props.onBelegPruefen}
            >
              Beleg prüfen <Kuerzel>Enter</Kuerzel>
            </button>
          </>
        }
      >
        <div className="zv2-links">
          <div className="zv2-meldung warnung" role="alert">
            <strong>Verkauf noch ungeklärt</strong>
            <span>{FK_WARNTEXT}</span>
          </div>
          <p className="zv2-text">
            Verkauf <strong>{props.ungeklaert}</strong> über {formatiereEuro(props.summeCent)} € –
            Belegnummer und Vorgang bleiben erhalten. In diesem Zustand gibt es kein Kassieren.
          </p>
        </div>
        <div className="zv2-block">
          <ZiffernTasten
            onTaste={() => undefined}
            gesperrt
            klasse="zv2-tasten"
            beschriftung="Ziffernblock (gesperrt)"
          />
          <span className="zv2-unterzeile">Ziffernblock gesperrt</span>
        </div>
      </ZahlungsHuelle>
    );
  }

  const schnell = schnellauswahl(props.summeCent);
  const gruppen = gruppiereTeile(props.teile);
  const anzahl = new Map(gruppen.map((g) => [g.wertCent, g.anzahl]));
  const fehlt = fehlbetragCent(props.summeCent, props.gegebenCent);
  const feldFehler = zustand === 'ungueltig' || zustand === 'unplausibel';
  const primaerText = props.laeuft
    ? 'Wird verarbeitet …'
    : props.gegebenCent === null
      ? 'Passend kassieren'
      : 'Zahlung bestätigen';

  function kassieren(): void {
    // gleiche Bedingung wie der bisherige Knopf; laeuft sperrt zusätzlich in BarDialog/Verkauf
    if (props.kassierbar) {
      props.onKassieren();
    }
  }

  return (
    <ZahlungsHuelle
      art="Barzahlung"
      betragLabel="Zu bezahlen"
      betragCent={props.summeCent}
      regel={regel}
      beschaeftigt={props.laeuft}
      klasse="zv2-bar"
      startFokus={feld}
      fokusSchluessel="eingabe"
      onTaste={(e) => {
        // Tastatur außerhalb des Felds (z. B. Fokus auf dem Dialog): Ziffern wie am Ziffernblock
        const ziel = e.target;
        if (gesperrt || e.altKey || e.ctrlKey || e.metaKey || ziel instanceof HTMLInputElement) {
          return;
        }
        if (ziel instanceof HTMLButtonElement || ziel instanceof HTMLAnchorElement) {
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!e.repeat) {
            kassieren();
          }
          return;
        }
        const t = e.key === 'Escape' ? null : tastaturZuZiffer(e.key);
        if (t !== null) {
          e.preventDefault();
          props.onZiffer(t);
        }
      }}
      fuss={
        <>
          <button
            type="button"
            className="taste zweit zv2-abbrechen"
            disabled={props.laeuft}
            aria-keyshortcuts="Escape"
            onClick={props.onAbbrechen}
          >
            Abbrechen <Kuerzel>Esc</Kuerzel>
          </button>
          <button
            type="button"
            className="taste gross zv2-primaer"
            disabled={!props.kassierbar}
            aria-keyshortcuts="Enter"
            aria-describedby={statusId}
            onClick={kassieren}
          >
            {primaerText} <Kuerzel>Enter</Kuerzel>
          </button>
        </>
      }
    >
      <div className="zv2-feld">
        <label htmlFor={feldId}>Gegeben</label>
        <div className={feldFehler ? 'zv2-eingabe fehler' : 'zv2-eingabe'}>
          <input
            ref={feld}
            id={feldId}
            // Touch-Ziffernblock statt Bildschirmtastatur (Figma FINAL-B1); Tastatur geht ebenso
            inputMode="none"
            autoComplete="off"
            placeholder="leer = passend"
            value={props.gegebenText}
            readOnly={props.laeuft}
            aria-invalid={feldFehler || zustand === 'zu_niedrig'}
            aria-describedby={statusId}
            onChange={(e) => {
              props.onEingabe(e.target.value);
            }}
            onKeyDown={(e) => {
              // wie bisher: nur bei genug gegebenem Betrag, nie durch gehaltene Taste
              if (e.key === 'Enter' && !e.repeat && props.kassierbar) {
                props.onKassieren();
              }
            }}
          />
          <span className="zv2-einheit" aria-hidden="true">
            €
          </span>
        </div>
      </div>

      <div className="zv2-schnell" role="group" aria-label="Schnellauswahl gegeben">
        {schnell.map((b) => {
          const aktiv = props.gegebenCent === b.cent && props.teile.length === 0;
          return (
            <button
              key={b.cent}
              type="button"
              className={aktiv ? 'zv2-wahl aktiv' : 'zv2-wahl'}
              aria-pressed={aktiv}
              disabled={props.laeuft}
              title={b.passend ? `Passend: ${formatiereEuro(b.cent)} €` : undefined}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                props.onSetze(b.cent);
              }}
            >
              {b.text}
            </button>
          );
        })}
      </div>

      <div className="zv2-scheine">
        <div className="zv2-scheine-kopf">
          <button
            type="button"
            className="zv2-link"
            aria-expanded={scheineOffen}
            aria-controls={scheinId}
            disabled={props.laeuft}
            onClick={() => {
              setScheineOffen((o) => !o);
            }}
          >
            {scheineOffen ? '– Scheine und Münzen zuklappen' : '+ Scheine und Münzen addieren'}
          </button>
          {props.teile.length > 0 && (
            <span className="zv2-addiert">
              Addiert:{' '}
              {gruppen.map((g) => `${String(g.anzahl)} × ${euroKurz(g.wertCent)}`).join(' · ')}
            </span>
          )}
        </div>
        {scheineOffen && (
          <div
            id={scheinId}
            className="zv2-scheine-feld"
            role="group"
            aria-label="Scheine und Münzen"
          >
            {[...SCHEINE_V2, ...MUENZEN_V2].map((cent) => {
              const n = anzahl.get(cent) ?? 0;
              return (
                <button
                  key={cent}
                  type="button"
                  className={n > 0 ? 'zv2-geld aktiv' : 'zv2-geld'}
                  disabled={props.laeuft}
                  aria-label={`${euroKurz(cent)} dazu${n > 0 ? `, bisher ${String(n)}` : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={() => {
                    props.onTippe(cent);
                  }}
                >
                  {n > 0 ? `${String(n)}× ` : ''}
                  {euroKurz(cent)}
                </button>
              );
            })}
            {props.teile.length > 0 && (
              <button
                type="button"
                className="zv2-geld zv2-zurueck"
                disabled={props.laeuft}
                onClick={props.onLetztesZurueck}
              >
                Letztes zurück
              </button>
            )}
          </div>
        )}
      </div>

      <div className="zv2-status" id={statusId}>
        {zustand === 'verarbeitung' ? (
          <div className="zv2-meldung marke" role="status">
            <strong>Zahlung wird verarbeitet …</strong>
            <span>
              Beleg wird am Server erstellt. Esc, Enter, F2, F3, Ziffernblock und Tasten sind
              gesperrt.
            </span>
          </div>
        ) : zustand === 'ungueltig' ? (
          <div className="zv2-meldung gefahr" role="alert">
            <strong>Betrag nicht lesbar – bitte prüfen (Scanner?).</strong>
          </div>
        ) : zustand === 'unplausibel' ? (
          <div className="zv2-meldung gefahr" role="alert">
            <strong>Gegebener Betrag ungewöhnlich hoch – bitte prüfen (Scanner?).</strong>
          </div>
        ) : zustand === 'zu_niedrig' ? (
          <div className="zv2-meldung warnung" role="alert">
            <strong>Gegebener Betrag reicht nicht.</strong>
          </div>
        ) : null}
      </div>

      <div
        className={
          fehlt !== null && !feldFehler
            ? 'zv2-rueckgeld fehlt'
            : props.rueckgeldCent !== null && !feldFehler
              ? 'zv2-rueckgeld'
              : 'zv2-rueckgeld leer'
        }
        aria-live="polite"
      >
        {fehlt !== null && !feldFehler ? (
          <>
            <span className="zv2-rueckgeld-label">Es fehlen</span>
            <strong className="zv2-rueckgeld-betrag">{formatiereEuro(fehlt)} €</strong>
          </>
        ) : props.rueckgeldCent !== null && !feldFehler ? (
          <>
            <span className="zv2-rueckgeld-label">Rückgeld</span>
            <strong className="zv2-rueckgeld-betrag">
              {formatiereEuro(props.rueckgeldCent)} €
            </strong>
            {props.rueckgeldCent > 0 && (
              <span className="zv2-stueckelung" aria-label="Rückgeld-Stückelung">
                Stückelung: {stueckelungText(props.rueckgeldCent)}
              </span>
            )}
          </>
        ) : (
          <>
            <span className="zv2-rueckgeld-label">Rückgeld</span>
            <strong className="zv2-rueckgeld-betrag" aria-label="kein Rückgeld">
              —
            </strong>
            {zustand === 'start' && (
              <span className="zv2-stueckelung">Feld leer = passend bezahlt, kein Rückgeld.</span>
            )}
          </>
        )}
      </div>

      <div className="zv2-block">
        <ZiffernTasten
          onTaste={props.onZiffer}
          gesperrt={props.laeuft}
          klasse="zv2-tasten"
          beschriftung="Ziffernblock gegeben"
          fokusHalten
        />
        <span className="zv2-unterzeile">
          {props.laeuft ? 'Ziffernblock gesperrt' : 'Touch-Ziffernblock · Tastatur geht ebenso'}
        </span>
      </div>
    </ZahlungsHuelle>
  );
}
